import React, { useCallback, useMemo, useState } from 'react';

import { SiteFooter } from '../../../components/SiteFooter/SiteFooter';
import { SiteNav } from '../../../components/SiteNav/SiteNav';
import { ProductCarousel } from '../components/ProductCarousel';
import { ProductDetails } from '../components/ProductDetails';
import type { ProductPageData, SerializedVariant } from '../types';

export default function ProductPage(data: ProductPageData) {
    const {
        csrfToken,
        shopId,
        colorGroups,
        selectedColorGroupIdx: initialColorIdx,
        selectedAdditionalImageIdx,
        selectedVariantId: initialVariantId,
        product,
        athlete,
        endorsements,
        splashRedesignEnabled,
    } = data;

    const [colorIdx, setColorIdx] = useState<number>(initialColorIdx ?? 0);
    const [variantId, setVariantId] = useState<number | null>(initialVariantId);

    const activeColorGroup = colorGroups[colorIdx] ?? null;

    const selectedVariant: SerializedVariant | null = useMemo(() => {
        if (!activeColorGroup) { return null; }
        const matchById = activeColorGroup.variants.find((v) => v.id === variantId);
        if (matchById) { return matchById; }
        return activeColorGroup.variants[0] ?? null;
    }, [activeColorGroup, variantId]);

    const handleSelectColorGroup = useCallback((idx: number) => {
        setColorIdx(idx);
        const group = colorGroups[idx];
        if (group && group.variants.length > 0) {
            setVariantId(group.variants[0].id);
        }
    }, [colorGroups]);

    const handleSelectVariant = useCallback((id: number) => {
        setVariantId(id);
    }, []);

    return (
        <div className="pdp-page">
            <div className="pdp-nav">
                <SiteNav floating redesignEnabled={splashRedesignEnabled} />
            </div>
            <main className="pdp-main">
                <div className="pdp-layout">
                    <div className="pdp-layout__carousel">
                        <ProductCarousel
                            colorGroups={colorGroups}
                            selectedColorGroupIdx={colorIdx}
                            selectedAdditionalImageIdx={selectedAdditionalImageIdx}
                            onSelectColorGroup={handleSelectColorGroup}
                            productName={product.name}
                        />
                    </div>
                    <div className="pdp-layout__details">
                        <ProductDetails
                            product={product}
                            athlete={athlete}
                            colorGroups={colorGroups}
                            selectedColorGroupIdx={colorIdx}
                            onSelectColorGroup={handleSelectColorGroup}
                            activeColorGroup={activeColorGroup}
                            selectedVariant={selectedVariant}
                            onSelectVariant={handleSelectVariant}
                            endorsements={endorsements}
                            shopId={shopId}
                        />
                    </div>
                </div>
            </main>
            <SiteFooter csrfToken={csrfToken} redesignEnabled={splashRedesignEnabled} />
        </div>
    );
}
